import { useEffect, useRef, useState } from "react";

/** A point on the screen (the viewport's pixels) */
export interface ScreenPoint { x: number; y: number }
/** The box the dot flies to (an element's `getBoundingClientRect()`) */
export interface ScreenBox { top: number; right: number; bottom: number; left: number }

/** The flight, Omnalingo's to the letter (the founder, 2026-10-07: "that one is perfect"): 650 ms on this curve, the dot shrinking to 0.35 */
export const TOUR_FLIGHT_MS = 650;
const FLIGHT_EASE = "cubic-bezier(0.22, 0.61, 0.36, 1)";
/** The landing: the button grows to 1.12 with a ring in the accent that fades, 600 ms */
export const TOUR_PULSE_MS = 600;
/** How long the tip stays when nobody clicks */
export const TOUR_TIP_MS = 3000;
/** the tip's fade in and out: the kit's 150 ms */
const FADE_MS = 150;

type Phase = "flying" | "landed";

/** The tour's goodbye on every end (the founder, 2026-10-07: "there is a dot animation when the step is closed at any time and it flies to the
 *  menu. i want that", ported from Omnalingo's `flyToHelpCenter`): a dot in the accent flies from where the tour's popover was to the centre of
 *  the account button (where Get help lives), shrinking as it goes; when it lands it is gone and `target` (the button) pulses, wearing
 *  `ob-tour-pulse` meanwhile. `tip` (the first completion only) then shows under the button for `TOUR_TIP_MS`, or until the next click or key,
 *  and `onDone` follows. With reduced motion there is no flight and no pulse: only the tip, when there is one. `still` draws the dot (at `from`) and the
 *  tip as they look, with no timer (the kit page). All motion runs in script (the Web Animations API), so no CSS animation exists for it; the
 *  colours come from the tokens (`ob-tour-dot`, `ob-tour-tip`, `ob-tour-pulse` in `styles/tour.css`). */
export function TourFlight({ from, to, target, tip, onDone, still = false }: { from: ScreenPoint; to: ScreenBox; /** the element that pulses on landing */ target?: HTMLElement | null; /** the words under the button; none: no tip */ tip?: string; onDone: () => void; still?: boolean }) {
  const dot = useRef<HTMLSpanElement>(null);
  const tipEl = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>(() => (still || reducedMotion() ? "landed" : "flying"));
  const land = { x: (to.left + to.right) / 2, y: (to.top + to.bottom) / 2 };

  useEffect(() => {
    const el = dot.current;
    if (phase !== "flying" || !el) return;
    if (typeof el.animate !== "function") { setPhase("landed"); return; }
    const flight = el.animate([
      { transform: `translate(${from.x - land.x}px, ${from.y - land.y}px) scale(1)`, opacity: 1 },
      { transform: "translate(0, 0) scale(0.35)", opacity: 0.9 },
    ], { duration: TOUR_FLIGHT_MS, easing: FLIGHT_EASE, fill: "forwards" });
    flight.onfinish = () => setPhase("landed");
    return () => flight.cancel();
  }, []); // one flight per mount: the points are where things stood when the tour ended

  useEffect(() => {
    if (still || phase !== "landed") return;
    const stopPulse = pulse(target);
    if (!tip) { const t = window.setTimeout(onDone, reducedMotion() ? 0 : TOUR_PULSE_MS); return () => { window.clearTimeout(t); stopPulse(); }; }
    fade(tipEl.current, 0, 1);
    let out = 0;
    const leave = () => { window.clearTimeout(out); out = window.setTimeout(onDone, fade(tipEl.current, 1, 0)); };
    const t = window.setTimeout(leave, TOUR_TIP_MS);
    const gone = () => { window.clearTimeout(t); onDone(); };
    window.addEventListener("pointerdown", gone, true);
    window.addEventListener("keydown", gone, true);
    return () => { window.clearTimeout(t); window.clearTimeout(out); stopPulse(); window.removeEventListener("pointerdown", gone, true); window.removeEventListener("keydown", gone, true); };
  }, [still, phase, tip, target, onDone]);

  return (
    <>
      {(phase === "flying" || still) && <span ref={dot} className="ob-tour-dot" style={still ? { left: from.x, top: from.y } : { left: land.x, top: land.y }} aria-hidden="true" />}
      {tip && <div ref={tipEl} className={phase === "landed" ? "ob-tour-tip" : "ob-tour-tip ob-tour-tip--waiting"} style={{ top: to.bottom + 10, left: to.right }} role="status">{phase === "landed" ? tip : ""}</div>}
    </>
  );
}

/** The landing pulse on the button (Omnalingo's lifebuoy pulse): scale 1 → 1.12 → 1 with a ring from the accent (the class's
 *  `--ob-tour-ring`) fading out, `TOUR_PULSE_MS`. Nothing with reduced motion. Returns the way to stop it early. */
function pulse(el: HTMLElement | null | undefined): () => void {
  if (!el || reducedMotion() || typeof el.animate !== "function") return () => {};
  el.classList.add("ob-tour-pulse");
  const ring = getComputedStyle(el).getPropertyValue("--ob-tour-ring").trim() || "transparent";
  const a = el.animate([
    { transform: "scale(1)", boxShadow: `0 0 0 0 ${ring}` },
    { transform: "scale(1.12)", boxShadow: "0 0 0 8px transparent", offset: 0.6 },
    { transform: "scale(1)", boxShadow: "0 0 0 0 transparent" },
  ], { duration: TOUR_PULSE_MS, easing: "ease-out" });
  const done = () => el.classList.remove("ob-tour-pulse");
  a.onfinish = done;
  return () => { a.cancel(); done(); };
}

/** the tip's fade (none with reduced motion); answers how long it takes */
function fade(el: HTMLElement | null, a: number, b: number): number {
  if (!el || reducedMotion() || typeof el.animate !== "function") return 0;
  el.animate([{ opacity: a }, { opacity: b }], { duration: FADE_MS, easing: "ease-out", fill: "forwards" });
  return FADE_MS;
}

const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
